'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { RunnerRequestDataAdapter } from '../../adapter';
import { type JSONContent } from '@tiptap/core';
import {
  getDiscoveredWorktreeDisplayName,
  osTypeValues,
  runnerTypeValues,
  type ActiveWorktree,
  type AgentConfig,
  type Daemon,
  type DaemonTrigger,
  type DaemonTriggerResolvedSettings,
  type DiscoveredWorktreeListItem,
  type OsType,
  type ProjectRepository,
  type ProjectRepositoryBranch,
  type ResolveDaemonTriggerSettingsInput,
  type MergedRunnerModelsByType,
  type RunnerType,
} from '../lib/api';
import { isApiError } from '../lib/apiError';
import {
  activeConcurrencyWarningReasonFromVerdict,
  buildCheckoutIdByAgentConfigId,
  evaluateActiveTriggerConflict,
  isActiveDaemonTriggerStatus,
  type ActiveTriggerConflictResult,
} from '../lib/daemonTriggerConflict';
import { getRunnerTypeLabel } from '../components/daemonTriggerDetailUtils';
import { type SelectOption } from '../components/Select';
import { BitbucketIcon, GitBranchIcon, GitHubIcon, GitLabIcon } from '../components/icons/ProviderIcons';
import { getEffortLevelLabel, getSupportedEffortLevels } from '../lib/runnerEffort';

import { isDaemonOnline, DEFAULT_DAEMON_ONLINE_THRESHOLD_MS } from '../lib/daemonOnline';
import { resolveEffectiveEngines } from '../lib/daemonEngines';
import {
  getCompatibleAgentConfigsForDaemon,
  isDaemonTriggerSelectableAgentConfig,
  scopeAgentConfigsToSelectedDaemon,
} from '../lib/daemonTriggerAgentSelection';
const daemonRunnerTypes = [...runnerTypeValues];
const worktreeUnsupportedProviders = new Set<ProjectRepository['provider']>(['MANUAL']);

// 형상관리 대상이 아닌 provider. LINEAR는 이슈 트래커 팀을 리포지토리로 등록한 것이라
// remoteUrl도 브랜치도 없다 — worktree를 못 만드는 정도가 아니라 리포지토리 선택지가 될 수 없다.
// MANUAL은 remoteUrl을 가진 실제 저장소일 수 있으므로 여기 넣지 않는다(비활성 표시로 남긴다).
const nonVcsProviders = new Set<ProjectRepository['provider']>(['LINEAR']);

const isVcsRepository = (repository: ProjectRepository) => !nonVcsProviders.has(repository.provider);

// 리포지토리 셀렉트의 벤더 글리프. GitLab만 브랜드 컬러 SVG라 currentColor 상속을 받지 않는다.
// MANUAL은 특정 벤더가 아니므로 중립적인 브랜치 아이콘을 쓴다.
const getRepositoryProviderIcon = (provider: ProjectRepository['provider']) => {
  const className = 'h-3.5 w-3.5';
  if (provider === 'GITHUB') return <GitHubIcon className={className} />;
  if (provider === 'GITLAB') return <GitLabIcon className={className} />;
  if (provider === 'BITBUCKET') return <BitbucketIcon className={className} />;
  return <GitBranchIcon className={className} />;
};
const discoveredWorktreeOptionPrefix = 'discovered:';

export const toDiscoveredWorktreeOptionValue = (id: string) => `${discoveredWorktreeOptionPrefix}${id}`;

// 기존 사용 셀렉트를 저장소/박스 2단으로 나누기 위한 정규화 형태.
// 관리형(ActiveWorktree)과 발견형(DiscoveredWorktreeListItem)이 필드 구성이 달라 한 모양으로 맞춘다.
type ExistingWorktreeEntry = {
  optionValue: string;
  repositoryId: string;
  repositoryLabel: string;
  repositoryIcon: ReactNode;
  boxLabel: string;
};

export const getDiscoveredWorktreeOptionLabel = (worktree: DiscoveredWorktreeListItem) => {
  const displayName = getDiscoveredWorktreeDisplayName(worktree, worktree.id.slice(0, 8));
  const daemonIdentity = worktree.daemonLabel ?? worktree.daemonId.slice(0, 8);
  // remote URL은 repositoryName과 정보가 중복되고 자격증명(userinfo)이 노출되며,
  // truncate 시 정작 구분자인 박스명을 밀어내므로 라벨에서 제외한다.
  return `${displayName} · ${worktree.repositoryName} · ${daemonIdentity}`;
};

export const findSelectedDiscoveredWorktree = (
  worktrees: DiscoveredWorktreeListItem[],
  optionValue: string,
): DiscoveredWorktreeListItem | null => {
  if (!optionValue.startsWith(discoveredWorktreeOptionPrefix)) return null;
  const id = optionValue.slice(discoveredWorktreeOptionPrefix.length);
  return worktrees.find((worktree) => worktree.id === id && worktree.status === 'AVAILABLE') ?? null;
};

export const scopeDiscoveredWorktreesToSelectedDaemon = <T extends Pick<DiscoveredWorktreeListItem, 'daemonId'>>(
  worktrees: T[],
  selectedDaemonId: string,
): T[] => {
  if (!selectedDaemonId) {
    return [];
  }

  return worktrees.filter((worktree) => worktree.daemonId === selectedDaemonId);
};

// 서버/다른 미러와 판정 로직을 공유하기 위해 daemonTriggerConflict에서 재노출한다.
export { isActiveDaemonTriggerStatus };

export type BranchInputMode = 'dropdown' | 'manual';

export type BranchFailureReason =
  | 'PROJECT_RECONNECT_REQUIRED'
  | 'PROJECT_REPOSITORY_FORBIDDEN'
  | 'CONNECTION_REQUIRED'
  | 'PERSONAL_RECONNECT_REQUIRED'
  | 'PERSONAL_REPOSITORY_FORBIDDEN'
  | 'REMOTE_URL_INVALID'
  | null;

const projectReconnectCodes = new Set([
  'GITHUB_PROJECT_ACCESS_TOKEN_INVALID',
  'GITLAB_PROJECT_ACCESS_TOKEN_INVALID',
  'BITBUCKET_PROJECT_ACCESS_TOKEN_INVALID',
]);
const projectForbiddenCodes = new Set([
  'GITHUB_PROJECT_ACCESS_FORBIDDEN',
  'GITLAB_PROJECT_ACCESS_FORBIDDEN',
  'BITBUCKET_PROJECT_ACCESS_FORBIDDEN',
]);
const connectionRequiredCodes = new Set(['GITHUB_NOT_CONNECTED', 'GITLAB_NOT_CONNECTED', 'BITBUCKET_NOT_CONNECTED']);
const personalReconnectCodes = new Set([
  'GITHUB_ACCESS_TOKEN_INVALID',
  'GITLAB_ACCESS_TOKEN_INVALID',
  'BITBUCKET_ACCESS_TOKEN_INVALID',
]);
const personalForbiddenCodes = new Set([
  'GITHUB_ACCESS_FORBIDDEN',
  'GITLAB_ACCESS_FORBIDDEN',
  'BITBUCKET_ACCESS_FORBIDDEN',
]);

export const getBranchFailureReason = (error: unknown): BranchFailureReason => {
  if (!isApiError(error) || !error.errorDetailCode) return null;
  if (projectReconnectCodes.has(error.errorDetailCode)) return 'PROJECT_RECONNECT_REQUIRED';
  if (projectForbiddenCodes.has(error.errorDetailCode)) return 'PROJECT_REPOSITORY_FORBIDDEN';
  if (connectionRequiredCodes.has(error.errorDetailCode)) return 'CONNECTION_REQUIRED';
  if (personalReconnectCodes.has(error.errorDetailCode)) return 'PERSONAL_RECONNECT_REQUIRED';
  if (personalForbiddenCodes.has(error.errorDetailCode)) return 'PERSONAL_REPOSITORY_FORBIDDEN';
  if (error.errorDetailCode === 'PROJECT_REPOSITORY_REMOTE_URL_INVALID') return 'REMOTE_URL_INVALID';
  return null;
};

export const shouldFallbackToManualBranchInput = (
  repository: Pick<ProjectRepository, 'provider'> | null,
  error: unknown,
): boolean => {
  if (repository?.provider === 'MANUAL') {
    return true;
  }

  return getBranchFailureReason(error) !== null;
};

const getOsTypeIcon = (osType: OsType | null | undefined) => {
  if (osType === 'MACOS') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.2 12.5c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-2-1.5-.2-2.8.9-3.6.9s-2-.9-3.2-.9c-1.7 0-3.2 1-4.1 2.5-1.8 3.1-.4 7.8 1.3 10.2.8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.9-.1 0-2.7-1.1-2.8-4.2Z" />
        <path d="M13.8 5.3c.7-.9 1.2-2.1 1-3.3-1 .1-2.2.7-2.9 1.6-.6.8-1.2 2-1 3.2 1.1.1 2.2-.6 2.9-1.5Z" />
      </svg>
    );
  }

  if (osType === 'WINDOWS') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M3 5.2 10.7 4v7H3V5.2Zm8.8-1.3L21 2.5V11h-9.2V3.9ZM3 12.9h7.7v7L3 18.8v-5.9Zm8.8 0H21v8.5l-9.2-1.3v-7.2Z" />
      </svg>
    );
  }

  if (osType === 'LINUX') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 2.5c-2.2 0-4 2.3-4 5.1 0 1.4.4 2.8 1.1 3.7-.6.6-1.1 1.6-1.1 2.9 0 .7.2 1.3.4 1.9l-1.9 1.2c-.7.4-1 1.2-.7 1.9l.3.8c.2.6.9 1 1.6.8l2.6-.7.6 1.3c.2.5.8.8 1.3.8s1-.3 1.3-.8l.6-1.3 2.6.7c.7.2 1.4-.2 1.6-.8l.3-.8c.3-.7 0-1.5-.7-1.9l-1.9-1.2c.3-.6.4-1.2.4-1.9 0-1.3-.5-2.3-1.1-2.9.7-.9 1.1-2.3 1.1-3.7 0-2.8-1.8-5.1-4-5.1Zm-1.6 5.1c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm3.2 0c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm-3.1 3.4h3c-.1.8-.7 1.4-1.5 1.4s-1.4-.6-1.5-1.4Z" />
      </svg>
    );
  }

  return null;
};

// 에이전트 후보 판정은 홈 피드 드롭과 공유하는 SSOT라 별도 모듈에 둔다(재export로 기존 import 경로 유지).
export { getCompatibleAgentConfigsForDaemon, isDaemonTriggerSelectableAgentConfig, scopeAgentConfigsToSelectedDaemon };

export const isAgentConfigCompatibleWithDaemon = (
  config: Pick<AgentConfig, 'osType'>,
  daemonOsType: OsType | null | undefined,
) => {
  if (!config.osType) {
    return false;
  }

  if (!daemonOsType || !osTypeValues.includes(daemonOsType)) {
    return true;
  }

  return config.osType === daemonOsType;
};

/// 실행 가능한 엔진 후보 SSOT.
/// 러너의 유효 엔진(수동 지정 → 신선한 자동 탐지)으로 먼저 좁히고, 공유 러너면 공유 단위 allowedEngines로 한 번 더 좁힌다.
/// 두 목록 모두 "빈 배열 = 제한 없음" 규약을 따르며, 서버 인가도 `resolveEffectiveEngines`로 동일한 술어를 강제한다.
export const getSupportedRunnerTypesForDaemon = (
  daemon:
    | (Pick<Daemon, 'supportedEngines'> &
        Partial<Pick<Daemon, 'detectedEngines' | 'detectedEnginesAt'>> & { allowedEngines?: RunnerType[] })
    | null
    | undefined,
): RunnerType[] => {
  if (!daemon) {
    return daemonRunnerTypes;
  }

  const effectiveEngines = resolveEffectiveEngines(daemon);
  const supportedRunnerTypes =
    effectiveEngines.length === 0
      ? daemonRunnerTypes
      : daemonRunnerTypes.filter((runnerType) => effectiveEngines.includes(runnerType));

  if (!daemon.allowedEngines || daemon.allowedEngines.length === 0) {
    return supportedRunnerTypes;
  }

  return supportedRunnerTypes.filter((runnerType) => daemon.allowedEngines!.includes(runnerType));
};

export const getAvailableModelsForDaemon = <T extends { availableDaemonIds?: string[] | null }>(
  models: T[],
  selectedDaemonId: string | null | undefined,
): T[] => {
  if (!selectedDaemonId) {
    return models;
  }

  return models.filter(
    (model) => model.availableDaemonIds == null || model.availableDaemonIds.includes(selectedDaemonId),
  );
};

export const getModelsForRunnerContext = <
  T extends { availableDaemonIds?: string[] | null; value: string; label: string },
>(
  runnerType: string,
  selectedDaemonId: string | null | undefined,
  catalogs: Record<string, Partial<Record<string, T[]>>> | undefined,
  fallbackModels: T[] | undefined,
): T[] => {
  if (selectedDaemonId && catalogs?.[selectedDaemonId]) {
    return catalogs[selectedDaemonId][runnerType] ?? [];
  }
  return getAvailableModelsForDaemon(fallbackModels ?? [], selectedDaemonId);
};

const getDaemonOptionLabel = (daemon: Daemon): string => {
  const baseLabel = daemon.label?.trim() ? daemon.label : daemon.id.slice(0, 8);
  const sharedOwner = 'ownerNickname' in daemon && typeof daemon.ownerNickname === 'string' ? daemon.ownerNickname : '';

  return sharedOwner ? `${baseLabel} · ${sharedOwner}` : baseLabel;
};

// 질문 섹션 파서는 @agentteams/core-constants가 SSOT다(api 목록 응답의 불리언 산출과 동일 판정).
// 기존 호출부·테스트가 이 훅 경로로 import하고 있으므로 여기서 그대로 재-export한다.
export { extractQuestionsForUser, extractQuestionsForUserMarkdown } from '@agentteams/core-constants';

export const getQuestionsForUserMarkdown = (
  trigger: Pick<DaemonTrigger, 'linkedCoActionHistory' | 'linkedCoAction'> | null | undefined,
): string | null => trigger?.linkedCoActionHistory?.content ?? trigger?.linkedCoAction?.content ?? null;

type DaemonTriggerModalMode = 'create' | 'continue';
type WorktreeSelectionMode = 'new' | 'existing';
type Translate = (key: string, values?: Record<string, string | number>) => string;

type UseDaemonTriggerModalArgs = {
  adapter: RunnerRequestDataAdapter<
    ProjectRepository,
    ProjectRepositoryBranch,
    ActiveWorktree,
    DiscoveredWorktreeListItem,
    ResolveDaemonTriggerSettingsInput,
    DaemonTriggerResolvedSettings
  >;
  token: string | null;
  selectedProjectId: string;
  enabled?: boolean;
  // 공유 러너(SharedDaemon)도 같은 목록으로 들어오므로 공유 단위 엔진 제한을 타입에 남긴다.
  daemons: Array<Daemon & { allowedEngines?: RunnerType[] }>;
  onlineThresholdMs?: number;
  agentConfigs: AgentConfig[];
  runnerModels:
    | Partial<
        Record<
          RunnerType,
          Array<{
            value: string;
            label: string;
            isCustom?: boolean;
            fastModeSupported?: boolean;
            supportedEffortLevels?: string[];
            availableDaemonIds?: string[] | null;
          }>
        >
      >
    | undefined;
  runnerModelCatalogs?: Record<string, MergedRunnerModelsByType>;
  activeDaemonTriggers: Array<{
    id: string;
    status: string;
    agentConfigId: string;
    targetDaemonId: string | null;
    claimedByDaemonId: string | null;
    planMode: boolean;
    useWorktree: boolean;
    // 작업 디렉터리 판정에 필요하다(워크트리를 쓰지 않는 실행은 둘 다 null).
    worktreeId: string | null;
    discoveredWorktreeId: string | null;
    runnerType: string;
  }>;
  mode: DaemonTriggerModalMode;
  continueSourceTrigger: DaemonTrigger | null;
  promptMaxLength: number;
  t: Translate;
};

export const useDaemonTriggerModal = ({
  adapter,
  token,
  selectedProjectId,
  enabled = true,
  daemons,
  onlineThresholdMs = DEFAULT_DAEMON_ONLINE_THRESHOLD_MS,
  agentConfigs,
  runnerModels,
  runnerModelCatalogs,
  activeDaemonTriggers,
  mode,
  continueSourceTrigger,
  promptMaxLength,
  t,
}: UseDaemonTriggerModalArgs) => {
  const [daemonPrompt, setDaemonPrompt] = useState<JSONContent | undefined>(undefined);
  const [daemonId, setDaemonId] = useState('');
  const [daemonAgentConfigId, setDaemonAgentConfigId] = useState('');
  const [daemonRunnerType, setDaemonRunnerType] = useState('');
  const [daemonModel, setDaemonModel] = useState('');
  const [daemonFastMode, setDaemonFastMode] = useState(false);
  // Effort는 fastMode와 별도 상태로 유지한다(모델 option 문자열에 인코딩하지 않음). '' = 모델 기본값.
  const [daemonEffort, setDaemonEffort] = useState('');
  const [daemonPlanMode, setDaemonPlanMode] = useState(false);
  const [daemonUseWorktree, setDaemonUseWorktree] = useState(false);
  const [worktreeSelectionMode, setWorktreeSelectionMode] = useState<WorktreeSelectionMode>('new');
  const [selectedExistingWorktreeId, setSelectedExistingWorktreeId] = useState('');
  const [daemonRepositoryId, setDaemonRepositoryId] = useState('');
  const [daemonBaseBranch, setDaemonBaseBranch] = useState('');
  const [isActiveRequestsSummaryExpanded, setIsActiveRequestsSummaryExpanded] = useState(false);
  const [worktreeWarningMessage, setWorktreeWarningMessage] = useState<string | null>(null);
  const [selectedUserConventionIds, setSelectedUserConventionIds] = useState<string[]>([]);

  const { data: projectRepositories = [], isFetching: isProjectRepositoriesFetching } = adapter.useRepositories(
    token,
    selectedProjectId,
    enabled,
  );
  const {
    data: discoveredWorktrees = [],
    isFetching: isDiscoveredWorktreesFetching,
    isError: isDiscoveredWorktreesError,
  } = adapter.useDiscoveredWorktrees(token, selectedProjectId);
  const availableDiscoveredWorktrees = useMemo(
    () =>
      scopeDiscoveredWorktreesToSelectedDaemon(
        discoveredWorktrees.filter((worktree) => worktree.status === 'AVAILABLE'),
        daemonId,
      ),
    [daemonId, discoveredWorktrees],
  );

  const daemonTriggerSelectableConfigs = useMemo(
    () => agentConfigs.filter(isDaemonTriggerSelectableAgentConfig),
    [agentConfigs],
  );
  const selectedDaemon = useMemo(() => daemons.find((daemon) => daemon.id === daemonId) ?? null, [daemons, daemonId]);
  // 신규 요청(create) 모드에서는 호환 가능한(선택 후보 + 해당 러너 바인딩 + osType 일치) 에이전트가
  // 1개 이상인 러너만 노출한다. 선택해도 실행할 에이전트가 없는 러너로 인한 혼선을 막는다.
  // 호환 판정은 드롭다운이 실제로 보여주는 기준(getCompatibleAgentConfigsForDaemon)과 동일해야 한다.
  // continue 모드는 원본 트리거에서 파생된 러너를 그대로 유지한다(기존 동작 보존).
  const selectableDaemons = useMemo(() => {
    if (mode !== 'create') {
      return daemons;
    }

    return daemons.filter(
      (daemon) =>
        getCompatibleAgentConfigsForDaemon(daemonTriggerSelectableConfigs, {
          id: daemon.id,
          osType: daemon.osType,
        }).length > 0,
    );
  }, [daemons, daemonTriggerSelectableConfigs, mode]);
  const selectedAgentConfig = useMemo(
    () => agentConfigs.find((config) => config.id === daemonAgentConfigId) ?? null,
    [agentConfigs, daemonAgentConfigId],
  );
  const boundDaemon = useMemo(
    () =>
      selectedAgentConfig?.targetDaemonId
        ? (daemons.find((d) => d.id === selectedAgentConfig.targetDaemonId) ?? null)
        : null,
    [daemons, selectedAgentConfig?.targetDaemonId],
  );
  const isBoundDaemonOffline = boundDaemon != null && !isDaemonOnline(boundDaemon.lastSeenAt, onlineThresholdMs);

  // 공유 러너 시나리오에서는 러너 소유자만 식별 가능하도록 ownerMemberId를 분리해 둡니다.
  const selectedDaemonOwnerMemberId = useMemo<string | null>(() => {
    if (!selectedDaemon) return null;
    const candidate = (selectedDaemon as { ownerMemberId?: unknown }).ownerMemberId;
    return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
  }, [selectedDaemon]);
  const isSelectedDaemonPlanModeOnly = useMemo(() => {
    if (!selectedDaemon) return false;
    const candidate = (selectedDaemon as { planModeOnly?: unknown }).planModeOnly;
    return candidate === true;
  }, [selectedDaemon]);
  // 선택된 에이전트 설정이 플랜 모드 전용이면 공유 러너 잠금과 동일하게 플랜 모드를 강제한다.
  const isSelectedAgentConfigPlanModeOnly = useMemo(
    () => selectedAgentConfig?.planModeOnly === true,
    [selectedAgentConfig],
  );
  const isPlanModeForced = isSelectedDaemonPlanModeOnly || isSelectedAgentConfigPlanModeOnly;

  const compatibleAgentConfigs = useMemo(
    () =>
      // 유효 에이전트가 있는 러너가 하나도 없으면 신규 요청에서 에이전트를 자동 선택하지 않는다.
      // (실행할 러너가 없는데 에이전트만 선택돼 보이는 모순 방지)
      mode === 'create' && selectableDaemons.length === 0
        ? []
        : getCompatibleAgentConfigsForDaemon(
            daemonTriggerSelectableConfigs,
            selectedDaemon
              ? {
                  id: selectedDaemon.id,
                  osType: selectedDaemon.osType,
                  ownerMemberId: selectedDaemonOwnerMemberId,
                }
              : null,
          ),
    [mode, selectableDaemons.length, daemonTriggerSelectableConfigs, selectedDaemon, selectedDaemonOwnerMemberId],
  );
  const isSelectedAgentConfigCompatible = useMemo(
    () => compatibleAgentConfigs.some((config) => config.id === daemonAgentConfigId),
    [compatibleAgentConfigs, daemonAgentConfigId],
  );
  const availableRunnerTypes = useMemo(() => getSupportedRunnerTypesForDaemon(selectedDaemon), [selectedDaemon]);
  const availableModels = useMemo(() => {
    if (!daemonRunnerType) {
      return [];
    }
    return getModelsForRunnerContext(
      daemonRunnerType,
      selectedDaemon?.id,
      runnerModelCatalogs,
      runnerModels?.[daemonRunnerType as RunnerType],
    );
  }, [daemonRunnerType, runnerModelCatalogs, runnerModels, selectedDaemon?.id]);
  // 리포지토리 선택지의 기준 목록. 형상관리가 아닌 항목은 아예 후보에서 뺀다.
  const vcsRepositories = useMemo(() => projectRepositories.filter(isVcsRepository), [projectRepositories]);
  const selectableWorktreeRepositories = useMemo(
    () => vcsRepositories.filter((repository) => !worktreeUnsupportedProviders.has(repository.provider)),
    [vcsRepositories],
  );
  const selectedDaemonRepository = useMemo(
    () => projectRepositories.find((repository) => repository.id === daemonRepositoryId) ?? null,
    [projectRepositories, daemonRepositoryId],
  );

  const {
    data: repositoryBranches = [],
    isFetching: isRepositoryBranchesFetching,
    isError: isRepositoryBranchesError,
    error: repositoryBranchesError,
  } = adapter.useBranches(
    token,
    selectedProjectId,
    daemonRepositoryId,
    daemonUseWorktree && worktreeSelectionMode === 'new',
  );

  const {
    data: activeWorktrees = [],
    isFetching: isActiveWorktreesFetching,
    isError: isActiveWorktreesError,
  } = adapter.useActiveWorktrees(token, daemonAgentConfigId, daemonUseWorktree && worktreeSelectionMode === 'existing');

  const resolvedSettingsInput = useMemo(
    () =>
      mode === 'create' && daemonAgentConfigId.length > 0
        ? {
            agentConfigId: daemonAgentConfigId,
            projectId: selectedProjectId || undefined,
            targetDaemonId: daemonId || undefined,
            runnerType: daemonRunnerType || undefined,
            model: daemonModel || undefined,
            fastMode: daemonFastMode,
            effort: daemonEffort || undefined,
            planMode: daemonPlanMode,
            useWorktree: daemonUseWorktree,
          }
        : null,
    [
      daemonAgentConfigId,
      daemonEffort,
      daemonFastMode,
      daemonId,
      daemonModel,
      daemonPlanMode,
      daemonRunnerType,
      daemonUseWorktree,
      mode,
      selectedProjectId,
    ],
  );
  const {
    data: resolvedSettings = null,
    error: resolvedSettingsError,
    isFetching: isResolvedSettingsFetching,
    isError: isResolvedSettingsError,
  } = adapter.useResolvedSettings(token, resolvedSettingsInput, {
    enabled: enabled && mode === 'create',
  });
  const displayedResolvedSettings = useMemo(() => {
    if (isResolvedSettingsError && isApiError(resolvedSettingsError) && resolvedSettingsError.errorDetailCode) {
      return {
        errors: [{ detail: resolvedSettingsError.errorDetailCode }],
      };
    }

    return resolvedSettings;
  }, [isResolvedSettingsError, resolvedSettings, resolvedSettingsError]);

  const branchInputMode = useMemo<BranchInputMode>(
    () =>
      shouldFallbackToManualBranchInput(selectedDaemonRepository, repositoryBranchesError) ? 'manual' : 'dropdown',
    [repositoryBranchesError, selectedDaemonRepository],
  );

  const branchFailureReason = useMemo(() => getBranchFailureReason(repositoryBranchesError), [repositoryBranchesError]);
  const showProjectRepositorySettingsCta =
    branchFailureReason === 'PROJECT_RECONNECT_REQUIRED' ||
    branchFailureReason === 'PROJECT_REPOSITORY_FORBIDDEN' ||
    branchFailureReason === 'CONNECTION_REQUIRED';

  const branchInputHelpMessage = useMemo(() => {
    if (!daemonUseWorktree || daemonRepositoryId.length === 0) {
      return null;
    }

    if (selectedDaemonRepository?.provider === 'MANUAL') {
      return t('daemonBranchManualRepositoryHint');
    }

    if (branchFailureReason === 'PROJECT_RECONNECT_REQUIRED') {
      return t('daemonBranchProjectReconnectRequired');
    }

    if (branchFailureReason === 'PROJECT_REPOSITORY_FORBIDDEN') {
      return t('daemonBranchProjectRepositoryForbidden');
    }

    if (branchFailureReason === 'CONNECTION_REQUIRED') {
      return t('daemonBranchConnectionRequired');
    }

    if (branchFailureReason === 'PERSONAL_RECONNECT_REQUIRED') {
      return t('daemonBranchPersonalReconnectRequired');
    }

    if (branchFailureReason === 'PERSONAL_REPOSITORY_FORBIDDEN') {
      return t('daemonBranchPersonalRepositoryForbidden');
    }

    if (branchFailureReason === 'REMOTE_URL_INVALID') {
      return t('daemonBranchRemoteUrlInvalid');
    }

    if (repositoryBranchesError) {
      return t('daemonWorktreeBranchUnavailable');
    }

    if (!isRepositoryBranchesFetching && !repositoryBranchesError && repositoryBranches.length === 0) {
      return t('daemonWorktreeBranchUnavailable');
    }

    return null;
  }, [
    daemonRepositoryId,
    daemonUseWorktree,
    branchFailureReason,
    isRepositoryBranchesFetching,
    repositoryBranches.length,
    repositoryBranchesError,
    selectedDaemonRepository,
    t,
  ]);

  const daemonOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('selectPlaceholder') },
      ...selectableDaemons.map((daemon) => {
        const isRunning = isDaemonOnline(daemon.lastSeenAt, onlineThresholdMs);

        return {
          value: daemon.id,
          label: getDaemonOptionLabel(daemon),
          showWarningIcon: !isRunning,
          leadingIcon: getOsTypeIcon(daemon.osType),
        };
      }),
    ],
    [selectableDaemons, onlineThresholdMs, t],
  );

  const daemonAgentConfigOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('selectPlaceholder') },
      ...compatibleAgentConfigs.map((config) => {
        // 공유 러너 소유자 Agent는 owner 이름 배지를 함께 표시해 자기 Agent와 구분합니다.
        const ownerLabel = config.createdByName ? ` · ${config.createdByName}` : '';

        return {
          value: config.id,
          label: `${config.name}${ownerLabel}`,
          leadingIcon: getOsTypeIcon(config.osType),
        };
      }),
    ],
    [compatibleAgentConfigs, t],
  );

  const daemonRunnerTypeOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('selectPlaceholder') },
      // 라벨 해석은 getRunnerTypeLabel 하나로 둔다. 여기서 삼항 체인을 다시 세우면
      // 마지막 else가 다음에 추가되는 엔진의 이름을 조용히 가로챈다(과거 KIMI_CLI 사례).
      ...availableRunnerTypes.map((runnerType) => ({
        value: runnerType,
        label: getRunnerTypeLabel(runnerType, t),
      })),
    ],
    [availableRunnerTypes, t],
  );

  const toFastModeModelOptionValue = (modelValue: string) => `__fast__:${modelValue}`;
  const decodeDaemonModelOptionValue = (optionValue: string): { model: string; fastMode: boolean } => {
    if (optionValue.startsWith('__fast__:')) {
      return {
        model: optionValue.slice('__fast__:'.length),
        fastMode: true,
      };
    }

    return {
      model: optionValue,
      fastMode: false,
    };
  };

  const daemonModelOptionValue = useMemo(
    () => (daemonFastMode && daemonModel.length > 0 ? toFastModeModelOptionValue(daemonModel) : daemonModel),
    [daemonFastMode, daemonModel],
  );

  const daemonModelOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('runnerModelClientDefault') },
      ...availableModels.flatMap((model) => {
        const label = model.isCustom ? `${model.label} ✦` : model.label;
        const options = [
          {
            value: model.value,
            label,
          },
        ];

        if (model.fastModeSupported) {
          options.push({
            value: toFastModeModelOptionValue(model.value),
            label: `⚡ ${label}`,
          });
        }

        return options;
      }),
    ],
    [availableModels, t],
  );

  // 명시적으로 선택된 모델의 지원 Effort 레벨만 노출한다. 모델 미선택(client default)이거나
  // 지원 레벨이 비어 있으면 Effort Select를 표시하지 않는다.
  const selectedModelOption = useMemo(
    () => (daemonModel.length > 0 ? (availableModels.find((model) => model.value === daemonModel) ?? null) : null),
    [availableModels, daemonModel],
  );
  const availableEffortLevels = useMemo<string[]>(
    () => getSupportedEffortLevels(daemonRunnerType, selectedModelOption?.supportedEffortLevels),
    [daemonRunnerType, selectedModelOption],
  );
  const showEffortSelect = availableEffortLevels.length > 0;
  const daemonEffortOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('daemonEffortModelDefault') },
      ...availableEffortLevels.map((level) => ({
        value: level,
        label: getEffortLevelLabel(level, t),
      })),
    ],
    [availableEffortLevels, t],
  );

  const daemonRepositoryOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('selectPlaceholder') },
      ...vcsRepositories.map((repository) => ({
        value: repository.id,
        label: repository.name,
        disabled: worktreeUnsupportedProviders.has(repository.provider),
        showWarningIcon: worktreeUnsupportedProviders.has(repository.provider),
        leadingIcon: getRepositoryProviderIcon(repository.provider),
      })),
    ],
    [vcsRepositories, t],
  );

  const daemonBranchOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: isRepositoryBranchesFetching ? t('daemonBranchLoading') : t('selectPlaceholder') },
      ...repositoryBranches.map((branch) => ({
        value: branch.name,
        label: branch.name,
      })),
    ],
    [isRepositoryBranchesFetching, repositoryBranches, t],
  );

  const selectedExistingWorktree = useMemo<ActiveWorktree | null>(
    () =>
      selectedExistingWorktreeId.startsWith(discoveredWorktreeOptionPrefix)
        ? null
        : (activeWorktrees.find((worktree) => worktree.worktreeId === selectedExistingWorktreeId) ?? null),
    [activeWorktrees, selectedExistingWorktreeId],
  );
  const selectedDiscoveredWorktree = useMemo(
    () => findSelectedDiscoveredWorktree(availableDiscoveredWorktrees, selectedExistingWorktreeId),
    [availableDiscoveredWorktrees, selectedExistingWorktreeId],
  );

  // 기존 사용 모드를 저장소 → 박스 2단으로 나누기 위해, 관리형/발견형을 한 모양으로 정규화한다.
  // 관리형(ActiveWorktree)은 repositoryId만 있고 이름이 없어 projectRepositories로 해석한다.
  const existingWorktreeEntries = useMemo<ExistingWorktreeEntry[]>(() => {
    const repositoryById = new Map(projectRepositories.map((repository) => [repository.id, repository]));
    const resolveRepositoryLabel = (repositoryId: string) =>
      repositoryById.get(repositoryId)?.name ?? t('daemonExistingWorktreeUnknownRepository');
    // 발견형 응답에는 provider가 없어 프로젝트 리포지토리로 해석한다. 못 찾으면 중립 아이콘.
    const resolveProviderIcon = (repositoryId: string) => {
      const provider = repositoryById.get(repositoryId)?.provider;
      return provider ? getRepositoryProviderIcon(provider) : <GitBranchIcon className="h-3.5 w-3.5" />;
    };

    return [
      ...activeWorktrees.map((worktree) => ({
        optionValue: worktree.worktreeId,
        repositoryId: worktree.repositoryId ?? '',
        repositoryLabel: resolveRepositoryLabel(worktree.repositoryId ?? ''),
        repositoryIcon: resolveProviderIcon(worktree.repositoryId ?? ''),
        boxLabel: worktree.alias
          ? `${worktree.alias} (${worktree.baseBranch ?? t('defaultSuffix')})`
          : `wt-${worktree.worktreeId} (${worktree.baseBranch ?? t('defaultSuffix')})`,
      })),
      ...availableDiscoveredWorktrees.map((worktree) => ({
        optionValue: toDiscoveredWorktreeOptionValue(worktree.id),
        repositoryId: worktree.repositoryId,
        repositoryLabel: worktree.repositoryName,
        repositoryIcon: resolveProviderIcon(worktree.repositoryId),
        // 저장소와 러너는 앞단에서 이미 선택되어 있으므로 박스 자체의 이름만 표시한다.
        boxLabel: getDiscoveredWorktreeDisplayName(worktree, worktree.id.slice(0, 8)),
      })),
    ];
  }, [activeWorktrees, availableDiscoveredWorktrees, projectRepositories, t]);

  const selectedExistingRepositoryId = useMemo(
    () =>
      existingWorktreeEntries.find((entry) => entry.optionValue === selectedExistingWorktreeId)?.repositoryId ??
      existingWorktreeEntries[0]?.repositoryId ??
      '',
    [existingWorktreeEntries, selectedExistingWorktreeId],
  );

  const existingRepositoryOptions = useMemo<SelectOption[]>(() => {
    const seen = new Map<string, SelectOption>();
    for (const entry of existingWorktreeEntries) {
      if (!seen.has(entry.repositoryId)) {
        seen.set(entry.repositoryId, {
          value: entry.repositoryId,
          label: entry.repositoryLabel,
          leadingIcon: entry.repositoryIcon,
        });
      }
    }
    if (seen.size === 0) {
      return [
        {
          value: '',
          label: isActiveWorktreesFetching ? t('daemonExistingWorktreeLoading') : t('selectPlaceholder'),
        },
      ];
    }
    return [...seen.values()];
  }, [existingWorktreeEntries, isActiveWorktreesFetching, t]);

  const existingWorktreeOptions = useMemo<SelectOption[]>(() => {
    const scoped = existingWorktreeEntries.filter((entry) => entry.repositoryId === selectedExistingRepositoryId);
    if (scoped.length === 0) {
      return [
        {
          value: '',
          label: isActiveWorktreesFetching ? t('daemonExistingWorktreeLoading') : t('selectPlaceholder'),
        },
      ];
    }
    return scoped.map((entry) => ({ value: entry.optionValue, label: entry.boxLabel }));
  }, [existingWorktreeEntries, isActiveWorktreesFetching, selectedExistingRepositoryId, t]);

  // 저장소를 바꾸면 그 저장소의 첫 박스로 선택을 옮긴다(빈 선택 상태를 만들지 않는다).
  const handleExistingRepositoryChange = useCallback(
    (repositoryId: string) => {
      const first = existingWorktreeEntries.find((entry) => entry.repositoryId === repositoryId);
      setSelectedExistingWorktreeId(first?.optionValue ?? '');
    },
    [existingWorktreeEntries],
  );

  const promptTextLength = useMemo(() => {
    const count = (node: JSONContent | undefined): number => {
      if (!node) return 0;
      if (node.type === 'text') return (node.text ?? '').length;
      if (node.content) return node.content.reduce((sum, child) => sum + count(child), 0);
      return 0;
    };

    return count(daemonPrompt);
  }, [daemonPrompt]);

  const isPromptOverLimit = promptTextLength > promptMaxLength;

  // 판정 대상이 되는 작업 디렉터리. 서버가 conflictCandidate에 채우는 값과 같은 규칙이다.
  // - 이어쓰기: 원본 트리거의 워크트리를 그대로 상속한다
  // - 기존 워크트리 재사용: 선택한 managed/discovered 워크트리
  // - 새 워크트리: 아직 발급 전이라 둘 다 null이고, 그래서 격리된 요청으로 판정된다
  const conflictWorkspace = useMemo(() => {
    if (mode === 'continue') {
      return {
        useWorktree: Boolean(continueSourceTrigger?.useWorktree),
        worktreeId: continueSourceTrigger?.worktreeId ?? null,
        discoveredWorktreeId: continueSourceTrigger?.discoveredWorktreeId ?? null,
      };
    }

    if (!daemonUseWorktree || worktreeSelectionMode !== 'existing') {
      return { useWorktree: daemonUseWorktree, worktreeId: null, discoveredWorktreeId: null };
    }

    return {
      useWorktree: true,
      worktreeId: selectedDiscoveredWorktree ? null : (selectedExistingWorktree?.worktreeId ?? null),
      discoveredWorktreeId: selectedDiscoveredWorktree?.id ?? null,
    };
  }, [
    continueSourceTrigger,
    daemonUseWorktree,
    mode,
    selectedDiscoveredWorktree,
    selectedExistingWorktree,
    worktreeSelectionMode,
  ]);

  const checkoutIdByAgentConfigId = useMemo(
    () => buildCheckoutIdByAgentConfigId(agentConfigs, daemonId),
    [agentConfigs, daemonId],
  );

  const activeDaemonTriggerConflict = useMemo<ActiveTriggerConflictResult>(() => {
    if (mode !== 'create' && mode !== 'continue') {
      return { verdict: 'NONE', reason: null };
    }

    return evaluateActiveTriggerConflict({
      agentConfigId: daemonAgentConfigId,
      daemonId,
      planMode: daemonPlanMode,
      ...conflictWorkspace,
      activeTriggers: activeDaemonTriggers,
      checkoutIdByAgentConfigId,
    });
  }, [
    activeDaemonTriggers,
    checkoutIdByAgentConfigId,
    conflictWorkspace,
    daemonAgentConfigId,
    daemonId,
    daemonPlanMode,
    mode,
  ]);

  const activeDaemonTriggerConflictVerdict = activeDaemonTriggerConflict.verdict;

  const activeConcurrencyWarningReason = activeConcurrencyWarningReasonFromVerdict(activeDaemonTriggerConflict);

  const daemonConflictSelectButtonClassName =
    activeDaemonTriggerConflictVerdict !== 'NONE'
      ? 'border-warning/60 bg-warning/5 text-warning hover:border-warning focus:border-warning focus:outline-warning/20'
      : undefined;

  const isSubmitDisabled = useMemo(() => {
    return (
      isPromptOverLimit ||
      (mode === 'continue'
        ? false
        : daemonId.length === 0 ||
          daemonAgentConfigId.length === 0 ||
          !isSelectedAgentConfigCompatible ||
          daemonRunnerType.length === 0 ||
          isResolvedSettingsError ||
          (daemonUseWorktree &&
            (worktreeSelectionMode === 'existing'
              ? selectedExistingWorktreeId.length === 0
              : daemonRepositoryId.length === 0 || daemonBaseBranch.length === 0)))
    );
  }, [
    daemonAgentConfigId,
    daemonBaseBranch,
    daemonId,
    daemonRepositoryId,
    daemonRunnerType,
    daemonUseWorktree,
    isResolvedSettingsError,
    isSelectedAgentConfigCompatible,
    isPromptOverLimit,
    mode,
    selectedExistingWorktreeId,
    worktreeSelectionMode,
  ]);

  useEffect(() => {
    // 신규 요청에서는 유효 에이전트가 있는 러너만 자동 선택 대상이다.
    // (숨겨진 러너가 기본값으로 잡혀 드롭다운 값과 옵션이 어긋나는 것을 막는다.)
    // continue 모드는 전체 러너를 그대로 사용한다.
    const availableDaemons = mode === 'create' ? selectableDaemons : daemons;

    if (availableDaemons.length === 0) {
      setDaemonId('');
      return;
    }

    if (!availableDaemons.some((daemon) => daemon.id === daemonId)) {
      // 직전에 고른 러너가 이 프로젝트에 아직 존재하면 그 선택을 우선 복원한다.
      const remembered = adapter.selection.getPreference(selectedProjectId)?.daemonId;
      const fallback =
        remembered && availableDaemons.some((daemon) => daemon.id === remembered) ? remembered : availableDaemons[0].id;
      setDaemonId(fallback);
    }
  }, [mode, selectableDaemons, daemons, daemonId, selectedProjectId, adapter.selection]);

  useEffect(() => {
    if (compatibleAgentConfigs.length === 0) {
      setDaemonAgentConfigId('');
      return;
    }

    if (!compatibleAgentConfigs.some((config) => config.id === daemonAgentConfigId)) {
      // 선택된 러너와 호환되는 에이전트 중 직전에 고른 것이 있으면 그 선택을 우선 복원한다.
      const remembered = adapter.selection.getPreference(selectedProjectId)?.agentConfigId;
      const fallback =
        remembered && compatibleAgentConfigs.some((config) => config.id === remembered)
          ? remembered
          : compatibleAgentConfigs[0].id;
      setDaemonAgentConfigId(fallback);
    }
  }, [compatibleAgentConfigs, daemonAgentConfigId, selectedProjectId, adapter.selection]);

  useEffect(() => {
    if (!selectedAgentConfig?.targetDaemonId) {
      return;
    }

    if (daemonId.length === 0 && daemons.some((d) => d.id === selectedAgentConfig.targetDaemonId)) {
      setDaemonId(selectedAgentConfig.targetDaemonId);
    }
  }, [daemonId.length, daemons, selectedAgentConfig?.targetDaemonId]);

  useEffect(() => {
    if (daemonAgentConfigId.length === 0 || availableRunnerTypes.length === 0) {
      setDaemonRunnerType('');
      return;
    }

    if (!availableRunnerTypes.includes(daemonRunnerType as RunnerType)) {
      setDaemonRunnerType('');
    }
  }, [availableRunnerTypes, daemonAgentConfigId, daemonRunnerType]);

  useEffect(() => {
    if (!daemonRunnerType) {
      setDaemonModel('');
      setDaemonFastMode(false);
      setDaemonEffort('');
      return;
    }

    if (
      daemonModel.length > 0 &&
      !availableModels.some((model) => model.value === daemonModel && (!daemonFastMode || model.fastModeSupported))
    ) {
      setDaemonModel('');
      setDaemonFastMode(false);
      setDaemonEffort('');
    }
  }, [availableModels, daemonFastMode, daemonModel, daemonRunnerType]);

  // 러너/모델이 바뀌어 현재 Effort가 새 모델의 지원 레벨에 없으면 즉시 초기화한다(stale 값 전송 방지).
  useEffect(() => {
    if (daemonEffort.length > 0 && !availableEffortLevels.includes(daemonEffort)) {
      setDaemonEffort('');
    }
  }, [availableEffortLevels, daemonEffort]);

  // 에이전트가 바뀌면 직전 에이전트 기준으로 골라둔 플랜 모드/RunnerBox 설정을 초기화한다.
  // (러너 전환으로 에이전트가 자동으로 바뀌는 경우도 포함. 최초 자동 선택/동일 값 재설정은 제외.)
  const previousAgentConfigIdRef = useRef(daemonAgentConfigId);
  useEffect(() => {
    const previousAgentConfigId = previousAgentConfigIdRef.current;
    previousAgentConfigIdRef.current = daemonAgentConfigId;

    if (previousAgentConfigId.length === 0 || previousAgentConfigId === daemonAgentConfigId) {
      return;
    }

    // 새 에이전트가 플랜 모드 전용이면 강제값(true)을 유지하고, 아니면 끈다.
    setDaemonPlanMode(isPlanModeForced);
    setDaemonUseWorktree(false);
    setWorktreeSelectionMode('new');
    setSelectedExistingWorktreeId('');
    setDaemonRepositoryId('');
    setDaemonBaseBranch('');
    setWorktreeWarningMessage(null);
  }, [daemonAgentConfigId, isPlanModeForced]);

  useEffect(() => {
    if (isPlanModeForced) {
      setDaemonPlanMode(true);
    }
  }, [isPlanModeForced]);

  // 선택된 에이전트 설정의 "러너 박스 사용 여부"(useWorktree)가 false면 RunnerBox(워크트리) 사용이 비활성화된다.
  // planModeOnly가 플랜 모드를 강제하는 것과 대칭으로, 이 경우 RunnerBox 토글을 끈 상태로 강제하고 비활성화한다.
  const isWorktreeUsageDisabled = useMemo(() => selectedAgentConfig?.useWorktree === false, [selectedAgentConfig]);

  useEffect(() => {
    if (!isWorktreeUsageDisabled) {
      return;
    }

    // 러너 박스 사용이 비활성화된 에이전트로 전환되면 이전에 켜둔 RunnerBox 선택값을 모두 해제한다.
    setDaemonUseWorktree(false);
    setWorktreeSelectionMode('new');
    setSelectedExistingWorktreeId('');
    setDaemonRepositoryId('');
    setDaemonBaseBranch('');
  }, [isWorktreeUsageDisabled]);

  useEffect(() => {
    if (!daemonUseWorktree || worktreeSelectionMode !== 'new') {
      return;
    }

    // 목록에서 제외된 비-형상관리 저장소가 선택 상태로 남지 않도록 vcsRepositories 기준으로 검증한다.
    if (vcsRepositories.length === 0) {
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    if (!daemonRepositoryId || !vcsRepositories.some((repository) => repository.id === daemonRepositoryId)) {
      setDaemonRepositoryId(selectableWorktreeRepositories[0]?.id ?? '');
      setDaemonBaseBranch('');
    }
  }, [daemonUseWorktree, daemonRepositoryId, vcsRepositories, selectableWorktreeRepositories, worktreeSelectionMode]);

  useEffect(() => {
    if (!daemonUseWorktree || worktreeSelectionMode !== 'existing') {
      return;
    }

    const optionValues = [
      ...activeWorktrees.map((worktree) => worktree.worktreeId),
      ...availableDiscoveredWorktrees.map((worktree) => toDiscoveredWorktreeOptionValue(worktree.id)),
    ];
    if (optionValues.length === 0) {
      setSelectedExistingWorktreeId('');
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    if (!optionValues.includes(selectedExistingWorktreeId)) {
      setSelectedExistingWorktreeId(optionValues[0] ?? '');
    }
  }, [
    activeWorktrees,
    availableDiscoveredWorktrees,
    daemonUseWorktree,
    selectedExistingWorktreeId,
    worktreeSelectionMode,
  ]);

  useEffect(() => {
    if (!daemonUseWorktree || worktreeSelectionMode !== 'existing') {
      return;
    }

    if (selectedDiscoveredWorktree) {
      setDaemonId(selectedDiscoveredWorktree.daemonId);
      setDaemonRepositoryId(selectedDiscoveredWorktree.repositoryId);
      setDaemonBaseBranch(selectedDiscoveredWorktree.branch ?? '');
      return;
    }
    setDaemonRepositoryId(selectedExistingWorktree?.repositoryId ?? '');
    setDaemonBaseBranch(selectedExistingWorktree?.baseBranch ?? '');
  }, [
    daemonUseWorktree,
    selectedDiscoveredWorktree,
    selectedExistingWorktree?.baseBranch,
    selectedExistingWorktree?.repositoryId,
    worktreeSelectionMode,
  ]);

  useEffect(() => {
    if (!daemonUseWorktree || worktreeSelectionMode !== 'new' || !selectedDaemonRepository) {
      return;
    }

    if (worktreeUnsupportedProviders.has(selectedDaemonRepository.provider)) {
      setWorktreeWarningMessage(t('daemonWorktreeManualWarning'));
      setDaemonUseWorktree(false);
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
    }
  }, [daemonUseWorktree, selectedDaemonRepository, t, worktreeSelectionMode]);

  useEffect(() => {
    if (!daemonUseWorktree || worktreeSelectionMode !== 'new' || daemonRepositoryId.length === 0) {
      setDaemonBaseBranch('');
      return;
    }

    if (branchInputMode === 'manual') {
      return;
    }

    if (daemonBaseBranch.length > 0 && repositoryBranches.some((branch) => branch.name === daemonBaseBranch)) {
      return;
    }

    setDaemonBaseBranch(repositoryBranches[0]?.name ?? '');
  }, [
    branchInputMode,
    daemonUseWorktree,
    daemonRepositoryId,
    daemonBaseBranch,
    repositoryBranches,
    worktreeSelectionMode,
  ]);

  const resetProjectScopedState = useCallback(() => {
    setDaemonId('');
    setDaemonAgentConfigId('');
    setDaemonRunnerType('');
    setDaemonModel('');
    setDaemonFastMode(false);
    setDaemonEffort('');
    setDaemonPlanMode(false);
    setDaemonUseWorktree(false);
    setWorktreeSelectionMode('new');
    setSelectedExistingWorktreeId('');
    setDaemonRepositoryId('');
    setDaemonBaseBranch('');
    setIsActiveRequestsSummaryExpanded(false);
    setWorktreeWarningMessage(null);
    setSelectedUserConventionIds([]);
  }, []);

  const previousProjectIdRef = useRef(selectedProjectId);

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    previousProjectIdRef.current = selectedProjectId;

    if (mode !== 'create' || !previousProjectId || previousProjectId === selectedProjectId) {
      return;
    }

    resetProjectScopedState();
  }, [mode, resetProjectScopedState, selectedProjectId]);

  const reset = () => {
    setDaemonPrompt(undefined);
    resetProjectScopedState();
  };

  const toggleUserConventionId = (conventionId: string, checked: boolean) => {
    setSelectedUserConventionIds((prev) => {
      if (checked) {
        if (prev.includes(conventionId)) return prev;
        return [...prev, conventionId];
      }
      return prev.filter((id) => id !== conventionId);
    });
  };

  const handleDaemonUseWorktreeChange = (checked: boolean) => {
    // 러너 박스 사용이 비활성화된 에이전트에서는 RunnerBox를 켤 수 없다.
    if (checked && isWorktreeUsageDisabled) {
      return;
    }

    if (!checked) {
      setDaemonUseWorktree(false);
      setWorktreeSelectionMode('new');
      setSelectedExistingWorktreeId('');
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    // LINEAR만 등록된 프로젝트는 "리포지토리 없음"과 같다 — MANUAL 경고로 새지 않도록 여기서 걸러낸다.
    if (vcsRepositories.length === 0) {
      setWorktreeWarningMessage(t('daemonWorktreeNoRepositoriesWarning'));
      setDaemonUseWorktree(false);
      setWorktreeSelectionMode('new');
      setSelectedExistingWorktreeId('');
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    } else if (selectableWorktreeRepositories.length === 0) {
      setWorktreeWarningMessage(t('daemonWorktreeManualWarning'));
      setDaemonUseWorktree(false);
      setWorktreeSelectionMode('new');
      setSelectedExistingWorktreeId('');
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    setDaemonUseWorktree(true);
  };

  const handleWorktreeSelectionModeChange = (value: WorktreeSelectionMode) => {
    setWorktreeSelectionMode(value);

    if (value === 'existing') {
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    setSelectedExistingWorktreeId('');
    setDaemonBaseBranch('');
  };

  const handleExistingWorktreeChange = (value: string) => {
    setSelectedExistingWorktreeId(value);
    const discovered = findSelectedDiscoveredWorktree(availableDiscoveredWorktrees, value);
    if (discovered) {
      setDaemonId(discovered.daemonId);
      setDaemonRepositoryId(discovered.repositoryId);
      setDaemonBaseBranch(discovered.branch ?? '');
    }
  };

  const handleDaemonRepositoryChange = (value: string) => {
    const repository = projectRepositories.find((item) => item.id === value) ?? null;
    if (repository && worktreeUnsupportedProviders.has(repository.provider)) {
      setWorktreeWarningMessage(t('daemonWorktreeManualWarning'));
      setDaemonUseWorktree(false);
      setDaemonRepositoryId('');
      setDaemonBaseBranch('');
      return;
    }

    setDaemonRepositoryId(value);
    setDaemonBaseBranch('');
  };

  const handleDaemonModelChange = (value: string) => {
    const decoded = decodeDaemonModelOptionValue(value);
    if (decoded.model !== daemonModel) {
      setDaemonEffort('');
    }
    setDaemonModel(decoded.model);
    setDaemonFastMode(decoded.fastMode);
  };

  const handleDaemonRunnerTypeChange = (value: string) => {
    if (value !== daemonRunnerType) {
      setDaemonEffort('');
    }
    setDaemonRunnerType(value);
  };

  // 사용자가 직접 러너/에이전트를 고른 경우에만 프로젝트별 선택을 기억한다.
  // (자동 기본값 선택이나 continue/edit 프리필 같은 프로그램적 변경은 저장하지 않는다.)
  const handleDaemonIdChange = (value: string) => {
    setDaemonId(value);
    if (mode === 'create' && value.length > 0) {
      adapter.selection.savePreference(selectedProjectId, { daemonId: value });
    }
  };

  const handleDaemonAgentConfigIdChange = (value: string) => {
    setDaemonAgentConfigId(value);
    if (mode === 'create' && value.length > 0) {
      adapter.selection.savePreference(selectedProjectId, { agentConfigId: value });
    }
  };

  return {
    daemonTriggerSelectableConfigs,
    compatibleAgentConfigs,
    daemonPrompt,
    setDaemonPrompt,
    daemonId,
    setDaemonId,
    handleDaemonIdChange,
    daemonAgentConfigId,
    setDaemonAgentConfigId,
    handleDaemonAgentConfigIdChange,
    daemonRunnerType,
    setDaemonRunnerType,
    daemonModel,
    setDaemonModel,
    daemonFastMode,
    setDaemonFastMode,
    daemonModelOptionValue,
    daemonEffort,
    setDaemonEffort,
    daemonEffortOptions,
    availableEffortLevels,
    showEffortSelect,
    daemonPlanMode,
    setDaemonPlanMode,
    daemonUseWorktree,
    setDaemonUseWorktree,
    worktreeSelectionMode,
    selectedExistingWorktreeId,
    selectedDiscoveredWorktreeId: selectedDiscoveredWorktree?.id ?? '',
    daemonRepositoryId,
    daemonBaseBranch,
    branchInputMode,
    branchInputHelpMessage,
    showProjectRepositorySettingsCta,
    setDaemonBaseBranch,
    isActiveRequestsSummaryExpanded,
    setIsActiveRequestsSummaryExpanded,
    worktreeWarningMessage,
    setWorktreeWarningMessage,
    daemonOptions,
    // 신규 요청 빈 상태 분기용: 연결된 러너 수와 유효 에이전트 보유 러너 수를 구분해 노출한다.
    // (daemonOptions는 placeholder 때문에 항상 1개 이상이라 빈 상태 판정에 쓸 수 없다.)
    hasConnectedDaemon: daemons.length > 0,
    selectableDaemonCount: selectableDaemons.length,
    daemonAgentConfigOptions,
    daemonRunnerTypeOptions,
    daemonModelOptions,
    daemonRepositoryOptions,
    daemonBranchOptions,
    existingWorktreeOptions,
    existingRepositoryOptions,
    selectedExistingRepositoryId,
    onExistingRepositoryChange: handleExistingRepositoryChange,
    isProjectRepositoriesFetching,
    isRepositoryBranchesFetching,
    isRepositoryBranchesError,
    isActiveWorktreesFetching: isActiveWorktreesFetching || isDiscoveredWorktreesFetching,
    isActiveWorktreesError: isActiveWorktreesError || isDiscoveredWorktreesError,
    hasRepositoryBranches: repositoryBranches.length > 0,
    promptTextLength,
    isPromptOverLimit,
    activeConcurrencyWarningReason,
    daemonConflictSelectButtonClassName,
    isBoundDaemonOffline,
    resolvedSettings: displayedResolvedSettings as DaemonTriggerResolvedSettings | null,
    isResolvedSettingsFetching,
    isResolvedSettingsError,
    isSelectedDaemonPlanModeOnly,
    isSelectedAgentConfigPlanModeOnly,
    isPlanModeForced,
    isWorktreeUsageDisabled,
    isSubmitDisabled,
    selectedUserConventionIds,
    setSelectedUserConventionIds,
    toggleUserConventionId,
    reset,
    resetProjectScopedState,
    handleDaemonUseWorktreeChange,
    handleWorktreeSelectionModeChange,
    handleExistingWorktreeChange,
    handleDaemonRepositoryChange,
    handleDaemonRunnerTypeChange,
    handleDaemonModelChange,
  };
};
